/**
 * FileAttachment — Tiptap block node pentru fișiere non-image criptate E2E.
 * Stocat ca <div data-file-asset-id="UUID" data-filename="..." data-mime="...">.
 * NodeView: card cu iconiță, nume, mărime, buton download (decrypt la click).
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Node, mergeAttributes } from '@tiptap/core';
import {
  NodeViewWrapper,
  ReactNodeViewRenderer,
  type NodeViewProps,
} from '@tiptap/react';

import { assetsApi } from '@/api/assets';
import { decryptWithWrappedKey } from '@/crypto/sodium';
import { useAuthStore } from '@/stores/auth';

export const FileAttachmentNode = Node.create({
  name: 'fileAttachment',
  group: 'block',
  atom: true,
  draggable: true,
  selectable: true,

  addAttributes() {
    return {
      assetId: {
        default: '',
        parseHTML: (el) => el.getAttribute('data-file-asset-id'),
      },
      filename: {
        default: 'fișier',
        parseHTML: (el) => el.getAttribute('data-filename'),
      },
      mime: {
        default: 'application/octet-stream',
        parseHTML: (el) => el.getAttribute('data-mime'),
      },
      size: {
        default: 0,
        parseHTML: (el) => Number(el.getAttribute('data-size') ?? '0'),
      },
    };
  },

  parseHTML() {
    return [{ tag: 'div[data-file-asset-id]' }];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'div',
      mergeAttributes({
        'data-file-asset-id': HTMLAttributes.assetId,
        'data-filename': HTMLAttributes.filename,
        'data-mime': HTMLAttributes.mime,
        'data-size': String(HTMLAttributes.size ?? ''),
      }),
      HTMLAttributes.filename,
    ];
  },

  addNodeView() {
    return ReactNodeViewRenderer(FileAttachmentView);
  },
});

function formatSize(bytes: number): string {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
}

function FileAttachmentView({ node }: NodeViewProps) {
  const { t } = useTranslation();
  const assetId = node.attrs.assetId as string;
  const filename = (node.attrs.filename as string) || t('notes_blocks.file.fallback_name');
  const mime = (node.attrs.mime as string) || 'application/octet-stream';
  const size = (node.attrs.size as number) || 0;
  const keyPair = useAuthStore((s) => s.keyPair);

  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const download = async () => {
    if (!keyPair) {
      setErr(t('notes_blocks.file.no_session_key'));
      return;
    }
    setErr(null);
    setBusy(true);
    try {
      const detail = await assetsApi.get(assetId);
      const plain = await decryptWithWrappedKey(detail, keyPair);
      const blob = new Blob([new Uint8Array(plain)], { type: detail.mime_type });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch (e) {
      setErr(e instanceof Error ? e.message : t('notes_blocks.file.download_error'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <NodeViewWrapper as="div" contentEditable={false} className="my-3">
      <div className="inline-flex items-center gap-3 border border-[var(--border-strong)] bg-[var(--bg-elevated)] rounded-md px-3 py-2 shadow-sm">
        <span className="text-2xl flex-shrink-0" aria-hidden>📎</span>
        <span className="flex flex-col min-w-0">
          <span className="text-sm font-medium truncate max-w-xs text-[var(--text)]">
            {filename}
          </span>
          <span className="text-[11px] text-[var(--text-muted)] font-mono">
            {mime}
            {size ? ` · ${formatSize(size)}` : ''}
          </span>
        </span>
        <button
          type="button"
          onClick={download}
          disabled={busy}
          className="btn-outline text-xs ml-2 flex-shrink-0"
        >
          {busy ? t('notes_blocks.file.decrypting') : t('notes_blocks.file.download')}
        </button>
      </div>
      {err && (
        <div className="text-xs text-red-600 dark:text-red-400 mt-1">{err}</div>
      )}
    </NodeViewWrapper>
  );
}
